import Padding from '@/components/padding/Padding';
import usePrefersDark from '@/hooks/usePrefersDark';
import { Box, Button, Heading, Text } from '@chakra-ui/react';
import React from 'react';
import { CiMobile3 } from 'react-icons/ci';
import { FaApplePay, FaHandsHelping } from 'react-icons/fa';
import { GrDocumentImage } from 'react-icons/gr';
import { IoIosPeople } from 'react-icons/io';
import { TbWorldWww } from 'react-icons/tb';

const AllFeatures = (): React.JSX.Element => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const sectionRef = React.useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);
  const [edgePadding, setEdgePadding] = React.useState<{ left: number; right: number }>({
    left: 0,
    right: 0,
  });

  const features: Array<{
    title: string;
    description: string;
    Icon: React.ComponentType<{ className?: string }>;
    gradient: string;
    accent: string;
    link: string;
  }> = [
    {
      title: 'Customised Club Website',
      description:
        'With Olympiq, your club gets a fully customisable website at no extra cost. You bring the domain - we handle the setup. No more maintaining your own site or hunting for a webmaster. Just a modern, flexible website that updates itself.',
      Icon: TbWorldWww,
      gradient: 'from-violet-500 to-fuchsia-500',
      accent: 'bg-violet-500',
      link: 'learn-more/customised-club-website',
    },
    {
      title: 'Content Management',
      description:
        'With Olympiq, you manage your content in one place - and it’s automatically pushed to your website and booking app. From session details to public club documents, everything stays consistent, up to date, and easy to control.',
      Icon: GrDocumentImage,
      gradient: 'from-rose-500 to-orange-400',
      accent: 'bg-rose-500',
      link: 'learn-more/content-management',
    },
    {
      title: 'Club Mobile App',
      description:
        'Get a personalised mobile app built for your club. Members can book sessions, view championship results, and even follow a live leaderboard. Admins can post updates and create polls. And best of all - one login for both the app and website.',
      Icon: CiMobile3,
      gradient: 'from-emerald-500 to-teal-400',
      accent: 'bg-emerald-500',
      link: 'learn-more/club-mobile-app',
    },
    {
      title: 'Membership Management',
      description:
        'Tired of chasing renewals? Olympiq handles it for you with automated emails and pre-signed payment links. Renewals become effortless, and if a membership isn’t renewed, access to the app updates automatically - no manual admin required.',
      Icon: IoIosPeople,
      gradient: 'from-blue-500 to-cyan-400',
      accent: 'bg-blue-500',
      link: 'learn-more/membership-management',
    },
    {
      title: 'Seamless Payments & Refunds',
      description:
        'Give your members a seamless payment experience - Google Pay and Apple Pay are automatically supported, and refunds are processed instantly if a booking is cancelled. Plus, you get paid out daily straight to your bank account.',
      Icon: FaApplePay,
      gradient: 'from-slate-600 to-gray-800',
      accent: 'bg-slate-600',
      link: 'learn-more/seamless-payments-refunds',
    },
    {
      title: 'Hands on Support',
      description:
        'You’re never on your own with Olympiq. We provide expert support every step of the way, from migrating your membership data to helping you get the most out of the platform.',
      Icon: FaHandsHelping,
      gradient: 'from-yellow-500 to-amber-400',
      accent: 'bg-yellow-500',
      link: 'learn-more/hands-on-support',
    },
  ];

  const updateArrows = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => updateArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  // Compute dynamic gutters so first/last card align with the content inside Padding
  const updateEdgePadding = React.useCallback(() => {
    const host = sectionRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const left = Math.max(0, rect.left);
    const right = Math.max(0, window.innerWidth - rect.right);
    setEdgePadding({ left, right });
  }, []);

  React.useEffect(() => {
    updateEdgePadding();
    window.addEventListener('resize', updateEdgePadding);
    const id = window.setTimeout(updateEdgePadding, 0); // recalc after fonts/layout settle
    return () => {
      window.removeEventListener('resize', updateEdgePadding);
      window.clearTimeout(id);
    };
  }, [updateEdgePadding]);

  const getCardScrollAmount = React.useCallback((): number => {
    const container = scrollRef.current;
    if (!container) return 360;
    const firstCard = container.querySelector('[data-card="true"]') as HTMLElement | null;
    const style = window.getComputedStyle(container);
    const gap =
      parseFloat(style.columnGap || style.gap || '0') ||
      parseFloat(style.getPropertyValue('gap') || '0') ||
      0;
    const width = firstCard?.getBoundingClientRect().width ?? 360;
    return width + gap;
  }, []);

  const scrollByOne = React.useCallback(
    (direction: 'left' | 'right') => {
      const el = scrollRef.current;
      if (!el) return;
      const amount = getCardScrollAmount();
      el.scrollBy({ left: direction === 'right' ? amount : -amount, behavior: 'smooth' });
    },
    [getCardScrollAmount],
  );

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByOne('right');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByOne('left');
      }
    },
    [scrollByOne],
  );

  const prefersDark = usePrefersDark();

  return (
    <Padding verticalPadding={10} backgroundColor={prefersDark ? undefined : '#16161f09'}>
      <Heading pt={10} fontSize="4xl" style={{ fontFamily: 'Bebas Neue' }}>
        Headline features
      </Heading>
      <Text fontSize="lg" style={{ fontFamily: 'Lato' }} className="mt-2 lg:w-[70%]">
        Scroll through our headline features to see how Olympiq can support your club. We’re
        constantly improving the platform, so there’s always something new to discover - and even
        more to demo. Got ideas? We love hearing from clubs about the features they need!
      </Text>

      <div ref={sectionRef} className="relative mt-8">
        {/* Edge-to-edge container */}
        <div style={{ width: '100vw', marginLeft: 'calc(50% - 50vw)' }}>
          {/* Scroll area */}
          <div
            ref={scrollRef}
            role="region"
            aria-label="Feature carousel"
            onMouseEnter={() => window.addEventListener('keydown', handleKeyDown)}
            onMouseLeave={() => window.removeEventListener('keydown', handleKeyDown)}
            onFocus={() => window.addEventListener('keydown', handleKeyDown)}
            onBlur={() => window.removeEventListener('keydown', handleKeyDown)}
            className="flex snap-x snap-mandatory overflow-x-auto no-scrollbar gap-6 pb-10"
            style={{
              scrollBehavior: 'smooth',
              paddingInline: `${edgePadding.left}px ${edgePadding.right}px`,
              scrollPaddingInline: `${edgePadding.left}px ${edgePadding.right}px`,
            }}
          >
            {features.map(({ title, description, Icon, gradient, accent, link }, idx) => (
              <article
                key={title + idx}
                data-card="true"
                className="snap-start shrink-0 w-[280px] sm:w-[320px] md:w-[360px] rounded-3xl bg-white/90 dark:bg-neutral-900/90 shadow-lg ring-1 ring-black/5 dark:ring-white/10 p-6"
              >
                <Box display="flex" flexDirection="column" gap={0} className="h-full">
                  <div
                    className={`h-12 w-12 rounded-full bg-gradient-to-br ${gradient} text-white flex items-center justify-center shadow-md`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>

                  <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
                    {title}
                  </h3>

                  <div
                    className="mt-2 h-0.5 w-10 rounded-full"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    {/* decorative accent is below using a separate div for better contrast */}
                  </div>
                  <div className={`mt-1 h-0.5 w-12 rounded-full ${accent}`} />

                  <p
                    style={{ flex: 1 }}
                    className="mt-3 text-sm leading-6 text-neutral-600 dark:text-neutral-300"
                  >
                    {description}
                  </p>

                  <Button asChild className="mt-4 w-full" variant="outline">
                    <a href={link}>Learn more</a>
                  </Button>
                </Box>
              </article>
            ))}
          </div>

          {/* Left control */}
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scrollByOne('left')}
            disabled={!canScrollLeft}
            className={`absolute left-0 top-1/2 -translate-y-1/2 ml-[-6px] rounded-full p-2 shadow-lg backdrop-blur bg-white/80 dark:bg-neutral-800/80 ring-1 ring-black/5 dark:ring-white/10 transition-opacity ${
              canScrollLeft ? 'opacity-100' : 'opacity-40 cursor-not-allowed'
            }`}
          >
            <span className="sr-only">Previous</span>
            {/* Using an icon from react-icons for the arrow to keep dependencies consistent */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              className="fill-none stroke-current text-neutral-700 dark:text-neutral-200"
            >
              <path
                d="M15 18l-6-6 6-6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Right control */}
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scrollByOne('right')}
            disabled={!canScrollRight}
            className={`absolute right-0 top-1/2 -translate-y-1/2 mr-[-6px] rounded-full p-2 shadow-lg backdrop-blur bg-white/80 dark:bg-neutral-800/80 ring-1 ring-black/5 dark:ring-white/10 transition-opacity ${
              canScrollRight ? 'opacity-100' : 'opacity-40 cursor-not-allowed'
            }`}
          >
            <span className="sr-only">Next</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              className="fill-none stroke-current text-neutral-700 dark:text-neutral-200"
            >
              <path
                d="M9 18l6-6-6-6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </Padding>
  );
};

export default AllFeatures;
